import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useSession } from "@/contexts/SessionContext";
import { useProfile } from "@/contexts/ProfileContext";
import { useAuthModal } from "@/contexts/AuthModalContext";
import { logPlatformEvent } from "@/hooks/usePlatformEvents";
import { trackAddToWishlist } from "@/lib/analytics";
import { trackAssetAnalyticsEvent } from "@/lib/analytics/events";
import { queryKeys } from "@/lib/query-keys";
import { toast } from "sonner";
import { humanizeError } from "@/lib/error-humanizer";

interface UseWishlistToggleOptions {
  assetId: string;
  assetTitle?: string;
  /** What the buyer would pay now (`listingPrice(asset).amount`), for analytics. */
  price?: number;
}

/**
 * The one wishlist toggle. Every heart for an asset — browse cards, the asset
 * page header, the purchase card, the mobile bar — reads the same cached
 * per-asset status, so toggling one updates them all.
 */
export function useWishlistToggle({ assetId, assetTitle, price }: UseWishlistToggleOptions) {
  const { user } = useSession();
  const { profile } = useProfile();
  const { openAuthModal } = useAuthModal();
  const queryClient = useQueryClient();

  const statusKey = queryKeys.user.wishlistStatus(profile?.id, assetId);
  const listKey = queryKeys.user.wishlist(profile?.id);

  const { data: isInWishlist = false } = useQuery({
    queryKey: statusKey,
    queryFn: async () => {
      if (!profile?.id) return false;
      const { data, error } = await supabase
        .from("wishlists")
        .select("id")
        .eq("user_id", profile.id)
        .eq("asset_id", assetId)
        .maybeSingle();
      if (error) throw error;
      return !!data;
    },
    enabled: !!profile?.id && !!assetId,
    staleTime: Infinity,
    gcTime: 5 * 60 * 1000,
  });

  const { mutate: toggle, isPending: isToggling } = useMutation({
    mutationFn: async (nextState: boolean) => {
      if (!profile?.id) throw new Error("Not authenticated");
      if (nextState) {
        const { error } = await supabase.from("wishlists").insert({
          user_id: profile.id,
          asset_id: assetId,
        });
        if (error && error.code !== "23505") throw error;
      } else {
        const { error } = await supabase
          .from("wishlists")
          .delete()
          .eq("user_id", profile.id)
          .eq("asset_id", assetId);
        if (error) throw error;
      }
      return nextState;
    },
    onMutate: async (nextState) => {
      await queryClient.cancelQueries({ queryKey: statusKey });
      const previousStatus = queryClient.getQueryData<boolean>(statusKey);
      queryClient.setQueryData<boolean>(statusKey, nextState);
      return { previousStatus };
    },
    onError: (err, _nextState, ctx) => {
      if (ctx?.previousStatus !== undefined) {
        queryClient.setQueryData(statusKey, ctx.previousStatus);
      }
      toast.error(humanizeError(err, "updating your wishlist"));
    },
    onSuccess: (nextState) => {
      if (nextState) {
        // target_asset_id is what the seller funnel filters on
        // (api/seller/analytics/funnel).
        logPlatformEvent({
          event_type: "wishlist_added",
          actor_profile_id: profile?.id,
          target_asset_id: assetId,
          metadata: { asset_id: assetId, asset_title: assetTitle ?? null },
        });
        trackAddToWishlist({ id: assetId, title: assetTitle ?? assetId, price: price ?? 0 });
        trackAssetAnalyticsEvent({
          name: "asset_added_to_wishlist",
          payload: { asset_id: assetId },
        });
        toast.success("Added to wishlist");
      } else {
        toast.success("Removed from wishlist");
      }
    },
    onSettled: () => {
      // The per-asset status has staleTime: Infinity, so it must be
      // invalidated explicitly or the heart never refetches.
      queryClient.invalidateQueries({ queryKey: listKey });
      queryClient.invalidateQueries({ queryKey: statusKey });
    },
  });

  const handleToggle = (e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();

    if (!user || !profile) {
      openAuthModal({
        message: "Save to your wishlist",
        intent: { kind: "wishlist", assetId },
        context: assetTitle ? { title: assetTitle } : undefined,
      });
      return;
    }

    toggle(!isInWishlist);
  };

  return { isInWishlist, isToggling, toggle: handleToggle };
}
